/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { CircularProgress, Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

export interface CircularProgressComponentProps {
  id?: string;
  value?: number;
  minimum?: number;
  maximum?: number;
  size?: number | string;
  thickness?: number;
  trackColor?: string;
  progressColor?: string;
  trackThickness?: number;
  progressThickness?: number;
  cornerRadius?: "Square" | "Round";
  isIndeterminate?: boolean;
  showProgressValue?: boolean;
  // Annotation content - can be string or React node
  annotationContent?: string | React.ReactNode;
  animation?: {
    enable?: boolean;
    duration?: number;
  };
  // Callbacks - Syncfusion compatible
  progressCompleted?: (args: { value: number }) => void;
  valueChanged?: (args: { value: number }) => void;
  // Style props
  className?: string;
  cssClass?: string;
  style?: React.CSSProperties;
  // Spread into a `sx` object literal below, so only the plain-object form of
  // MUI's `SxProps` is accepted here (not the array / theme-callback forms).
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
}

export interface CircularProgressComponentRef {
  value: number;
  refresh: () => void;
}

const CircularProgressComponent = forwardRef<CircularProgressComponentRef, CircularProgressComponentProps>(
  (props, ref) => {
    const {
      id,
      value: propValue = 0,
      minimum = 0,
      maximum = 100,
      size = 120,
      thickness = 4,
      trackColor = "#e0e0e0",
      progressColor,
      trackThickness,
      progressThickness,
      cornerRadius = "Square",
      isIndeterminate = false,
      showProgressValue = false,
      annotationContent,
      animation,
      progressCompleted,
      valueChanged,
      className,
      cssClass,
      style,
      sx,
      children,
    } = props;

    const [value, setValue] = useState(propValue);

    useEffect(() => {
      setValue(propValue);
      if (valueChanged) {
        valueChanged({ value: propValue });
      }
      if (propValue >= maximum && progressCompleted) {
        progressCompleted({ value: propValue });
      }
    }, [propValue, maximum]);

    useImperativeHandle(ref, () => ({
      get value() {
        return value;
      },
      set value(val: number) {
        setValue(val);
      },
      refresh: () => {
        setValue((prev) => prev);
      },
    }));

    // Normalize value to 0-100 range
    const normalizedValue = ((value - minimum) / (maximum - minimum)) * 100;

    // Handle gradient colors
    const getProgressColor = () => {
      if (progressColor?.startsWith("url(")) {
        // Return default gradient for SVG references
        return undefined; // Will use gradient def below
      }
      return progressColor || "#6BBBE3";
    };

    const useGradient = progressColor?.startsWith("url(") || !progressColor;
    const gradientId = `circularGradient_${id || "default"}`;

    const actualThickness = progressThickness || thickness;
    const actualTrackThickness = trackThickness || thickness;

    const numericSize = typeof size === "string" ? parseInt(size) : size;

    return (
      <Box
        id={id}
        className={className || cssClass}
        style={style}
        sx={
          {
            position: "relative",
            display: "inline-flex",
            width: numericSize,
            height: numericSize,
            ...sx,
          } as SxProps<Theme>
        }
      >
        {/* SVG Gradient Definition */}
        {useGradient && (
          <svg width={0} height={0}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: "#6BBBE3" }} />
                <stop offset="100%" style={{ stopColor: "#3E8AB0" }} />
              </linearGradient>
            </defs>
          </svg>
        )}

        {/* Track (background circle) */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={numericSize}
          thickness={actualTrackThickness}
          sx={{
            position: "absolute",
            color: trackColor,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: cornerRadius === "Round" ? "round" : "butt",
            },
          }}
        />

        {/* Progress circle */}
        <CircularProgress
          variant={isIndeterminate ? "indeterminate" : "determinate"}
          value={normalizedValue}
          size={numericSize}
          thickness={actualThickness}
          sx={{
            position: "absolute",
            color: getProgressColor(),
            "& .MuiCircularProgress-circle": {
              strokeLinecap: cornerRadius === "Round" ? "round" : "butt",
              stroke: useGradient ? `url(#${gradientId})` : undefined,
              transition:
                animation?.enable !== false ? `stroke-dashoffset ${animation?.duration || 300}ms ease` : "none",
            },
          }}
        />

        {/* Center content */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            insetInlineStart: 0,
            bottom: 0,
            insetInlineEnd: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {annotationContent ? (
            typeof annotationContent === "string" ? (
              <div dangerouslySetInnerHTML={{ __html: annotationContent }} />
            ) : (
              annotationContent
            )
          ) : showProgressValue ? (
            <Typography variant="caption" component="div" color="text.secondary">
              {`${Math.round(normalizedValue)}%`}
            </Typography>
          ) : (
            children
          )}
        </Box>
      </Box>
    );
  },
);

CircularProgressComponent.displayName = "CircularProgressComponent";

export default CircularProgressComponent;
